import axios from 'axios';

const baseUrl = process.env.REACT_APP_DIFLEX_API_URL + "/tenants";

// Function to get all Tenants for users
export async function getAllTenantsForUsers(apiAccessToken) {
  try {
    const response = await axios.get(`${baseUrl}/Tenants/get-tenants-for-user`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiAccessToken}`
      }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response.data.message || 'Failed to fetch tenants');
  }
}

export async function getAllTenantsForAgreement(agreementPublicId, apiAccessToken) {
  try {
    const response = await axios.get(`${baseUrl}/Tenants/get-tenants-for-agreement/${agreementPublicId}`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiAccessToken}`
      }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response.data.message || 'Failed to fetch tenants');
  }
}

// Function to get all agreements for a user
/* export const getTenantsForUser = async (userId, apiAccessToken) => {
  try {
    const response = await axios.get(`${baseUrl}/tenants/${userId}`, {
      headers: {
        'Authorization': `Bearer ${apiAccessToken}`
      }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch tenants');
  }
}; */

// Function to get a specific tenant by tenantPublicId
export async function getTenant(tenantPublicId, apiAccessToken) {
  try {
    const response = await axios.get(`${baseUrl}/Tenants/get-tenant/${tenantPublicId}`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiAccessToken}`
      }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response.data.message || 'Failed to fetch tenant');
  }
}

// Function to create a new tenant
export async function createTenant(tenantData, apiAccessToken) {
  try {
    const response = await axios.post(`${baseUrl}/Tenants/create-tenant`, tenantData, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiAccessToken}`
      }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response.data.message || 'Failed to create tenant');
  }
}

// Function to update an existing tenant
export async function updateTenant(tenantData, apiAccessToken) {
  try {
    const response = await axios.put(`${baseUrl}/Tenants/update-tenant`, tenantData, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiAccessToken}`
      }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response.data.message || 'Failed to update tenant');
  }
}

// Function to delete an tenant by tenantPublicId
export async function deleteTenant(tenantPublicId, apiAccessToken) {
  try {
    const response = await axios.delete(`${baseUrl}/Tenants/delete-tenant/${tenantPublicId}`, {
      headers: {
        'Authorization': `Bearer ${apiAccessToken}`
      }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response.data.message || 'Failed to delete tenant');
  }
}

// LDAP Credentials
export async function getLdapCredentials(apiAccessToken) {
  try {
    const response = await axios.get(`${baseUrl}/Tenants/ldap-credentials/get-all-ldap-credentials`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiAccessToken}`
      }
    });
    
    return response.data;
  } catch (error) {
    throw new Error(error.response.data.message || 'Failed to fetch ldap credentials');
  }
}

export async function updateLdapCredentials(ldapData, apiAccessToken) {
  try {
    const response = await axios.put(`${baseUrl}/Tenants/ldap-credentials/update-ldap-credential`, ldapData, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiAccessToken}`
      }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response.data.message || 'Failed to update ladp credentials');
  }
}

export async function deleteLdapCredentials(ldapCredentialPublicId, apiAccessToken) {
  try {
    const response = await axios.delete(`${baseUrl}/Tenants/ldap-credentials/delete-ldap-credential/${ldapCredentialPublicId}`, {
      headers: {
        'Authorization': `Bearer ${apiAccessToken}`
      }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response.data.message || 'Failed to delete ldap credentials');
  }
}

/**
 * Fetches a tenant's customer-specific SCO form questions (PBI #320).
 *
 * @param {string} tenantPublicId - Public id of the tenant
 * @param {string} apiAccessToken - Bearer token for the Insight API
 * @returns {Promise<object[]>} The tenant's active questions in render order
 * @throws {Error} If the API request fails
 */
export async function getScoCustomQuestions(tenantPublicId, apiAccessToken) {
  try {
    const response = await axios.get(`${baseUrl}/Tenants/sco-custom-questions/by-tenant/${encodeURIComponent(tenantPublicId)}`, {
      headers: {
        'Authorization': `Bearer ${apiAccessToken}`
      }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch SCO custom questions');
  }
}

/**
 * Creates a customer-specific SCO form question (PBI #320). Admin only.
 *
 * @param {object} questionData - { tenantPublicId, label, questionType, options, sortOrder }
 * @param {string} apiAccessToken - Bearer token for the Insight API
 * @returns {Promise<object>} The created question
 * @throws {Error} If the API request fails
 */
export async function createScoCustomQuestion(questionData, apiAccessToken) {
  try {
    const response = await axios.post(`${baseUrl}/Tenants/sco-custom-questions/create-sco-custom-question`, questionData, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiAccessToken}`
      }
    });
    return response.data;
  } catch (error) {
    const detail = typeof error.response?.data === 'string' ? error.response.data : error.response?.data?.message;
    throw new Error(detail || 'Failed to create SCO custom question');
  }
}

/**
 * Updates a customer-specific SCO form question (PBI #320). Admin only.
 *
 * @param {object} questionData - { scoCustomQuestionPublicId, label, questionType, options, sortOrder }
 * @param {string} apiAccessToken - Bearer token for the Insight API
 * @returns {Promise<object>} The updated question
 * @throws {Error} If the API request fails
 */
export async function updateScoCustomQuestion(questionData, apiAccessToken) {
  try {
    const response = await axios.put(`${baseUrl}/Tenants/sco-custom-questions/update-sco-custom-question`, questionData, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiAccessToken}`
      }
    });
    return response.data;
  } catch (error) {
    const detail = typeof error.response?.data === 'string' ? error.response.data : error.response?.data?.message;
    throw new Error(detail || 'Failed to update SCO custom question');
  }
}

/**
 * Deletes a customer-specific SCO form question (PBI #320). Admin only.
 *
 * @param {string} scoCustomQuestionPublicId - Public id of the question
 * @param {string} apiAccessToken - Bearer token for the Insight API
 * @throws {Error} If the API request fails
 */
export async function deleteScoCustomQuestion(scoCustomQuestionPublicId, apiAccessToken) {
  try {
    const response = await axios.delete(`${baseUrl}/Tenants/sco-custom-questions/delete-sco-custom-question/${encodeURIComponent(scoCustomQuestionPublicId)}`, {
      headers: {
        'Authorization': `Bearer ${apiAccessToken}`
      }
    });
    return response.data;
  } catch (error) {
    const detail = typeof error.response?.data === 'string' ? error.response.data : error.response?.data?.message;
    throw new Error(detail || 'Failed to delete SCO custom question');
  }
}

/**
 * Fetches the values the customer's onboarding form offers beyond what its users already carry:
 * saved Office, Company and Department values (#357) and saved addresses (#358).
 *
 * @param {string} tenantPublicId - Public id of the tenant
 * @param {string} apiAccessToken - Bearer token for the Insight API
 * @returns {Promise<{listValues: object[], addresses: object[]}>} The saved values, hidden list values included
 * @throws {Error} If the API request fails
 */
export async function getScoFormValues(tenantPublicId, apiAccessToken) {
  try {
    const response = await axios.get(`${baseUrl}/ScoFormValues/by-tenant/${encodeURIComponent(tenantPublicId)}`, {
      headers: {
        'Authorization': `Bearer ${apiAccessToken}`
      }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch SCO form values');
  }
}

const changeScoListValue = async (action, change, apiAccessToken, failure) => {
  try {
    const response = await axios.post(`${baseUrl}/ScoFormValues/${action}`, change, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiAccessToken}`
      }
    });
    return response.data;
  } catch (error) {
    const detail = typeof error.response?.data === 'string' ? error.response.data : error.response?.data?.message;
    throw new Error(detail || failure);
  }
};

/**
 * Offers an Office, Company or Department value on the customer's onboarding form (#357), or
 * offers a removed one again. Admin only.
 *
 * @param {object} change - { tenantPublicId, field, value }
 * @param {string} apiAccessToken - Bearer token for the Insight API
 * @throws {Error} If the API request fails
 */
export const addScoListValue = (change, apiAccessToken) =>
  changeScoListValue('add-list-value', change, apiAccessToken, 'Failed to add the value');

/**
 * Removes a value from the customer's onboarding form, also while users still carry it (#357).
 * Admin only.
 *
 * @param {object} change - { tenantPublicId, field, value }
 * @param {string} apiAccessToken - Bearer token for the Insight API
 * @throws {Error} If the API request fails
 */
export const hideScoListValue = (change, apiAccessToken) =>
  changeScoListValue('hide-list-value', change, apiAccessToken, 'Failed to remove the value');

/**
 * Replaces a value with its corrected spelling on the customer's onboarding form (#357). Admin
 * only.
 *
 * @param {object} change - { tenantPublicId, field, value, newValue }
 * @param {string} apiAccessToken - Bearer token for the Insight API
 * @throws {Error} If the API request fails
 */
export const correctScoListValue = (change, apiAccessToken) =>
  changeScoListValue('correct-list-value', change, apiAccessToken, 'Failed to correct the value');
